/**
 * RelatedVaccinesPanel — bidirectional link surface.
 *
 * Drops into legacy EntityPage / TopicPage / SourcePage to show vaccines
 * that cross-link back into the Research Explorer. Makes the Vaccine
 * Explorer feel embedded in the broader TCP library rather than separate.
 *
 * Reusable: future explorers (Medicines, Chemicals) will register their
 * own "related X" panels alongside this one using the same pattern.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Syringe, ArrowUpRight } from "lucide-react";
import api from "../../lib/api";

export default function RelatedVaccinesPanel({ entityId = null, topicId = null, parentRef = null, className = "" }) {
    const [vaccines, setVaccines] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = entityId
            ? `/research/related-to-entity/${entityId}`
            : topicId
                ? `/research/related-to-topic/${topicId}`
                : null;
        if (!url) { setLoading(false); return; }
        api.get(url)
            .then(({ data }) => setVaccines(data.vaccines || []))
            .catch(() => setVaccines([]))
            .finally(() => setLoading(false));
    }, [entityId, topicId]);

    if (loading) return null;
    if (!vaccines.length) return null;

    return (
        <section
            className={`bg-[#111827] border border-[#243044] rounded-xl p-5 ${className}`}
            data-testid="related-vaccines-panel"
        >
            <div className="flex items-center gap-2 mb-3">
                <Syringe className="w-4 h-4 text-[#56CCF2]" />
                <h3 className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] font-semibold">Related vaccines</h3>
                <span className="text-[10px] text-[#64748B] ml-1">{vaccines.length}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {vaccines.map((v) => (
                    <Link
                        key={v.id || v.slug}
                        to={`/vaccines/v/${v.slug}`}
                        className="group flex items-center gap-2.5 bg-[#0B1220] border border-[#243044] hover:border-[#56CCF2]/50 rounded-md px-3 py-2 transition-colors"
                        data-testid={`related-vaccine-${v.slug}`}
                    >
                        <Syringe className="w-3.5 h-3.5 text-[#56CCF2]" />
                        <span className="text-sm text-white group-hover:text-[#56CCF2] transition-colors flex-1 truncate">{v.display_name}</span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-[#64748B] group-hover:text-[#56CCF2] transition-colors" />
                    </Link>
                ))}
            </div>
        </section>
    );
}
